import type { Effect } from 'redux-saga';
import { fork, takeEvery, put, select, call } from 'redux-saga/effects';
import { jsonOrError } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/http';
import { getPlan } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/plan';
import * as disabledSprintsActions from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/disabled-sprints/actions';
import * as externalSprintsActions from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/external-sprints/actions';
import type * as Api from '@atlassian/jira-portfolio-3-portfolio/src/common/api/types';
import type { SPRINT_STATES } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { urls } from './api';

export const GET_SPRINTS_FOR_IDS = 'command.sprint.GET_SPRINTS_FOR_IDS' as const;

type GetSprintsForIdsPayload = string[];
type GetSprintsForIdsAction = {
	type: typeof GET_SPRINTS_FOR_IDS;
	payload: GetSprintsForIdsPayload;
};

export type SprintType = (typeof SPRINT_STATES)[keyof typeof SPRINT_STATES] | 'PROJECTED';

export const getSprintsForIds = (payload: GetSprintsForIdsPayload): GetSprintsForIdsAction => ({
	type: GET_SPRINTS_FOR_IDS,
	payload,
});

export function* handleExternalSprintsInternal(
	allExternalSprints: Api.Sprint[], // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<Effect, void, any> {
	const disabledSprints: Api.Sprint[] = [];
	const externalSprints: Api.Sprint[] = [];
	allExternalSprints.forEach((sprint) => {
		if (sprint.isDisabled === true) {
			disabledSprints.push(sprint);
		} else {
			externalSprints.push(sprint);
		}
	});

	yield put(externalSprintsActions.add(externalSprints));
	yield put(disabledSprintsActions.add(disabledSprints));
}

export function* doGetSprintsForIds({
	payload, // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: GetSprintsForIdsAction): Generator<Effect, any, any> {
	const { id: planId, currentScenarioId: scenarioId } = yield select(getPlan);
	const response = yield* jsonOrError({
		url: urls.sprintsForIds,
		method: 'POST',
		body: { planId, scenarioId, itemKeys: payload },
	});
	if (response.ok) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const externalSprints = response.data as Api.Sprint[];
		yield call(handleExternalSprintsInternal, externalSprints);
	}
}

export function* populateExternalSprints(
	allSprints: Api.Sprint[],
	planSprints: Api.Sprint[], // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<Effect, void, any> {
	const planSprintIds: Set<string> = new Set(planSprints.map((it) => it.id));
	const externalSprints: Api.Sprint[] = allSprints.filter((it) => !planSprintIds.has(it.id));

	yield call(handleExternalSprintsInternal, externalSprints);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* watchGetSprintsForIds(): Generator<Effect, any, any> {
	yield takeEvery(GET_SPRINTS_FOR_IDS, doGetSprintsForIds);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, jira/import/no-anonymous-default-export
export default function* (): Generator<Effect, any, any> {
	yield fork(watchGetSprintsForIds);
}
